import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useLikedTracks } from '../hooks/useLikedTracks';
import { useQueue } from '../hooks/useQueue';
import { useHistory } from '../hooks/useHistory';
import { tracks } from '../data/tracks';
import { usePlayerStore } from '../stores/playerStore';

// PlayerContext: thin wrapper around playerStore.
// audioRef + useLikedTracks + useQueue + useHistory remain here (DOM/localStorage side-effects).
// Pure playback state (currentTrack, isPlaying, etc.) delegated to playerStore.

const PlayerActionsContext = createContext(null);
const PlayerStateContext = createContext(null);

export function PlayerProvider({ children }) {
  const {
    currentTrack,
    isPlaying,
    playlist,
    currentTrackIndex,
    currentTime,
    duration,
    volume,
    playTrack: storePlayTrack,
    setIsPlaying,
    setPlaylist,
    setCurrentTrackIndex: storeSetCurrentTrackIndex,
    setCurrentTime,
    setDuration,
    setVolume,
  } = usePlayerStore();

  const audioRef = useRef(null);
  // activeAudioElementRef: always points to the current HTMLAudioElement
  // (unwrapped from the ref-of-ref stored by registerAudioRef)
  const activeAudioElementRef = useRef(null);

  const { liked, isLiked, toggle: toggleLike } = useLikedTracks();
  const {
    queue,
    setQueue,
    remove: removeFromQueue,
    clear: clearQueue,
    addNext: addToQueue, // Ajoute en haut (devient la prochaine track)
    addToEnd: addToQueueEnd, // Ajoute en fin de file (rare)
    pop: popQueue,
  } = useQueue();
  const { history, log: logHistory } = useHistory();

  const recentlyPlayed = useMemo(
    () =>
      history
        .slice(0, 5)
        .map((h) => tracks.find((t) => t.id === h.trackId))
        .filter(Boolean),
    [history]
  );

  // ─── Actions ────────────────────────────────────────────────────────────────

  const playTrack = useCallback(
    (track, trackList, index) => {
      storePlayTrack(track, trackList, index);
      if (track?.id != null) logHistory(track.id);
      // eslint-disable-next-line -- react-hooks/exhaustive-deps: intentional partial deps
    },
    [logHistory]
  );

  const setTrackIndex = useCallback(
    (index) => {
      storeSetCurrentTrackIndex(index);
      // Read playlist freshly from the store (not the closure) to avoid
      // setCurrentTrack(undefined) when context state is one tick behind.
      const storePlaylist = usePlayerStore.getState().playlist;
      const next = storePlaylist[index];
      if (next) {
        usePlayerStore.getState().setCurrentTrack(next);
      }
    },
    [storeSetCurrentTrackIndex]
  );

  const seek = useCallback(
    (time) => {
      setCurrentTime(time);
      if (audioRef.current && audioRef.current.current) {
        audioRef.current.current.currentTime = time;
      }
    },
    [setCurrentTime]
  );

  const registerAudioRef = useCallback((ref) => {
    audioRef.current = ref;
    // Keep activeAudioElementRef in sync so EQ/spectrum consumers get the HTMLAudioElement directly
    activeAudioElementRef.current = ref?.current ?? null;
  }, []);

  const handleTimeUpdate = useCallback(
    (time, dur) => {
      setCurrentTime(time);
      setDuration(dur);
    },
    [setCurrentTime, setDuration]
  );

  // ─── Stable state value (split to avoid re-render cascade) ──────────────────

  const state = useMemo(
    () => ({
      currentTrack,
      isPlaying,
      playlist,
      currentTrackIndex,
      currentTime,
      duration,
      volume,
      queue,
      history,
      recentlyPlayed,
      liked,
      isLiked,
    }),
    [
      currentTrack,
      isPlaying,
      playlist,
      currentTrackIndex,
      currentTime,
      duration,
      volume,
      queue,
      history,
      recentlyPlayed,
      liked,
      isLiked,
    ]
  );

  const actions = useMemo(
    () => ({
      playTrack,
      setIsPlaying,
      setTrackIndex,
      setPlaylist,
      seek,
      setVolume,
      registerAudioRef,
      handleTimeUpdate,
      toggleLike,
      setQueue,
      removeFromQueue,
      clearQueue,
      addToQueue,
      addToQueueEnd,
      popQueue,
      // Stable ref to the active HTMLAudioElement — used by EQ and spectrum hooks
      activeAudioElementRef,
    }),
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: stable refs intentional
    []
  );

  return (
    <PlayerStateContext.Provider value={state}>
      <PlayerActionsContext.Provider value={actions}>
        {children}
      </PlayerActionsContext.Provider>
    </PlayerStateContext.Provider>
  );
}

export function usePlayerState() {
  const ctx = useContext(PlayerStateContext);
  if (!ctx) throw new Error('usePlayerState must be inside PlayerProvider');
  return ctx;
}

export function usePlayerActions() {
  const ctx = useContext(PlayerActionsContext);
  if (!ctx) throw new Error('usePlayerActions must be inside PlayerProvider');
  return ctx;
}

/** Convenience hook — returns both state and actions */
export function usePlayer() {
  return { ...usePlayerState(), ...usePlayerActions() };
}
